/**
 * Text hygiene for names and chat. Runs on the server (authoritative) and on
 * the client (so what you type previews the way others will see it).
 *
 * This is a floor, not a moderation system: it strips invisible/control
 * characters, caps lengths, masks a short list of slurs and strong words, and
 * blocks obvious link spam. The room's report → shadow-mute loop is the other
 * half (see server/src/room.ts).
 */

export const NAME_MIN = 2
export const NAME_MAX = 16
export const CHAT_MAX = 140

// Control chars, zero-width and bidi overrides, variation selectors, tag chars.
const INVISIBLE = /[\u0000-\u001f\u007f-\u009f­͏؜ᅟᅠ឴឵᠋-᠏​-‏‪-‮⁠-⁯ㅤ︀-️﻿ﾠ￰-￸]|\udb40[\udc00-\udc7f]/g
// Combining marks stacked deep ("zalgo"): keep at most two per base character.
const ZALGO = /(\p{M}{2})\p{M}+/gu

function clean(s: string): string {
  return s.normalize('NFC').replace(INVISIBLE, '').replace(ZALGO, '$1').replace(/\s+/g, ' ').trim()
}

/** Normalise for matching: leetspeak and repeated letters collapse. */
function fold(s: string): string {
  return leet(s).replace(/(.)+/g, '$1')
}

function leet(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/[@4]/g, 'a').replace(/[3]/g, 'e').replace(/[1!|]/g, 'i').replace(/[0]/g, 'o').replace(/[$5]/g, 's').replace(/[7]/g, 't')
    .replace(/(.)\1+/g, '$1')
}

// Kept deliberately short and strong: slurs and the words a kids-friendly
// hangout should never show. Folded at load, so "faggot" is stored as "fagot".
const ANYWHERE = ['faggot', 'retard', 'kike', 'chink', 'tranny', 'fuck', 'shit', 'whore', 'slut', 'hitler'].map(fold)
// These hide inside real words ("Nigeria", "spice", "Scunthorpe", "grape",
// "pedometer", "Sussex", "cucumber"), so they only count as the whole token.
const WHOLE = ['niger', 'spic', 'cunt', 'rape', 'pedo', 'nazi', 'fag', 'cock', 'dick', 'pussy', 'bitch', 'bastard', 'twat', 'wank', 'kys', 'porn', 'sex', 'nude', 'cum'].map(fold)

function hasBad(word: string): boolean {
  const f = fold(word).replace(/[^a-z]/g, '')
  if (!f) return false
  if (WHOLE.includes(f) || (f.endsWith('s') && WHOLE.includes(f.slice(0, -1)))) return true
  // The n-word folds to "niger"; its double g is what tells it from Niger and Nigeria.
  if (/ni+g{2,}/.test(leet(word))) return true
  return ANYWHERE.some((w) => f.includes(w))
}

/** Replace each offending token with sparkles of the same length. */
export function maskText(s: string): string {
  return s.replace(/[^\s]+/g, (tok) => (hasBad(tok) ? '✿'.repeat(Math.min(6, Math.max(3, tok.length))) : tok))
}

const LINK = /\b(?:https?:\/\/|www\.)\S+|\b[a-z0-9-]{2,}\.(?:com|net|org|gg|io|xyz|ru|ly|me|co|tk|top|link|app|dev|tv)\b(?:\/\S*)?/gi

/** A chat line as the room will show it, or null to drop it. */
export function cleanChat(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  let s = clean(raw.slice(0, CHAT_MAX * 4))
  if (!s) return null
  s = [...s].slice(0, CHAT_MAX).join('')
  s = s.replace(LINK, '[link]')
  // Shouting gets lower-cased beyond a short burst.
  if (s.length > 12 && s === s.toUpperCase() && /[A-Z]/.test(s)) s = s.toLowerCase()
  return maskText(s)
}

/** A display name, or null if it is not acceptable. */
export function cleanName(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const s = clean(raw.slice(0, 64))
  // Letters (any script), digits, space and a few joiners; no emoji spam or symbols.
  if (!/^[\p{L}\p{N}][\p{L}\p{N} _.'-]*$/u.test(s)) return null
  const n = [...s].length
  if (n < NAME_MIN || n > NAME_MAX) return null
  if (s.split(' ').some(hasBad) || hasBad(s.replace(/[ _.'-]/g, ''))) return null
  if (/^(?:admin|mod|moderator|staff|system|blobland|official)\b/i.test(s)) return null
  return s
}
